import { useEffect, useRef, useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { useSearchParams } from "react-router-dom";
import DeleteModal from "../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../components/popovers/actionPopover";
import APISearchInput from "../../../../components/search/APISearchInput";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import { LoanBenefitDto } from "../../../../models/loan-benefit.dto";
import { useGetEmployeeBand } from "../../../../services/employee-band.service";
import { useGetEmployee } from "../../../../services/employee.service";
import {
	useDeleteLoanBenefitGroup,
	useGetLoanBenefitGroup,
	useGetLoanBenefitGroupSearch,
} from "../../../../services/loan-benefit-group.servict";
import {
	useDeleteLoanBenefit,
	useGetLoanBenefit,
	useGetLoanBenefitSearch,
} from "../../../../services/loan-benefit.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import LoanBenefitGroupModalForm from "../modal/LoanBenefitGroupModalForm";
import LoanBenefitModalForm from "../modal/LoanBenefitModalForm";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import {
	PermissionChecker,
	PERMISSIONS,
	useCheckUserPermissions,
} from "../../../../helpers/usePermissionManager";
import {
	loanBenefitGroupColumns,
	loanBenefitGroupColumnVisibility,
	loanBenefitColumns,
	loanBenefitColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../models/table-configuration.dto";
import ExportLoanBenefitsModal from "../modal/ExportLoanBenefitsModal";
import { ChevronDown, Upload } from "lucide-react";
import { GoUpload } from "react-icons/go";
import { BsDownload } from "react-icons/bs";
import UploadLoanBenefit from "../modal/UploadLoanBenefit";

const LoanBenefitsView = () => {
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();

	// Tab state
	const [activeTab, setActiveTab] = useState<
		"loanBenefitGroups" | "loanBenefits"
	>("loanBenefitGroups");

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openLoanBenefitGroupModal, setOpenLoanBenefitGroupModal] =
		useState(false);
	const [openLoanBenefitModal, setOpenLoanBenefitModal] = useState(false);
	const [openUploadLoanBenefitModal, setOpenUploadLoanBenefitModal] =
		useState(false);
	const [openExportModal, setOpenExportModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [isUploadExportOpen, setIsUploadExportOpen] = useState(false);
	const uploadExportDropdownRef = useRef<HTMLDivElement | null>(null);
	const [searchParams, setSearchParams] = useSearchParams();

	// Column visibility for Loan Benefit Groups
	const {
		columnVisibilityModel: loanBenefitGroupColumnVisibilityModel,
		setColumnVisibilityModel: setLoanBenefitGroupColumnVisibilityModel,
	} = useColumnVisibilityModel(loanBenefitGroupColumnVisibility);

	// Column visibility for Loan Benefits
	const {
		columnVisibilityModel: loanBenefitColumnVisibilityModel,
		setColumnVisibilityModel: setLoanBenefitColumnVisibilityModel,
	} = useColumnVisibilityModel(loanBenefitColumnVisibility);

	// Loan Benefit Groups table
	const { mutate: searchLoanBenefitGroups } = useGetLoanBenefitGroupSearch(currentCompany?.id as number);
	const {
		tableAPIRef: loanBenefitGroupTableAPIRef,
		fetchedTableData: fetchedLoanBenefitGroupData,
		tableData: loanBenefitGroupTableData,
		isLoading: loanBenefitGroupLoading,
		pageCount: loanBenefitGroupPageCount,
		setTableData: setLoanBenefitGroupTableData,
	} = useManageTableWithSearchParams<LoanBenefitDto>({
		fetchHook: (options) =>
			useGetLoanBenefitGroup(currentCompany?.id as number, options),
		options: {},
	});

	// Loan Benefits table
	const { mutate: searchLoanBenefits } = useGetLoanBenefitSearch();
	const {
		tableAPIRef: loanBenefitTableAPIRef,
		fetchedTableData: fetchedLoanBenefitData,
		tableData: loanBenefitTableData,
		isLoading: loanBenefitLoading,
		pageCount: loanBenefitPageCount,
		setTableData: setLoanBenefitTableData,
	} = useManageTableWithSearchParams<LoanBenefitDto>({
		fetchHook: useGetLoanBenefit,
		options: {
			companyId: currentCompany?.id,
		},
	});

	// Fetched data for forms
	const { data: loanBenefitGroups } = useGetLoanBenefitGroup(
		currentCompany?.id as number,
		{
			limit: APP_CONFIG.PAGE_LIMIT,
			orderBy: "createdAt:desc" as any,
		} as any,
	);
	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});
	const { data: employeeBands } = useGetEmployeeBand({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: "name:asc" as any,
	});

	const handleClose = () => setAnchorEl(null);

	const handleOpenLoanBenefitGroupModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenLoanBenefitGroupModal(true);
	};

	const handleOpenLoanBenefitModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenLoanBenefitModal(true);
	};

	const handleTabChange = (tab: "loanBenefitGroups" | "loanBenefits") => {
		setActiveTab(tab);
		const updated = new URLSearchParams(searchParams);
		updated.delete("page");
		updated.delete("pageSize");
		setSearchParams(updated);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteLoanBenefitGroup } = useDeleteLoanBenefitGroup();
	const { mutate: deleteLoanBenefit } = useDeleteLoanBenefit();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		if (activeTab === "loanBenefitGroups") {
			deleteLoanBenefitGroup(
				{ companyId: currentCompany?.id as number, id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		} else {
			deleteLoanBenefit(
				{ id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				uploadExportDropdownRef.current &&
				!uploadExportDropdownRef.current.contains(event.target as Node)
			) {
				setIsUploadExportOpen(false);
			}
		};

		if (isUploadExportOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isUploadExportOpen]);

	const handleOpenExportModal = () => {
		setIsUploadExportOpen(false);
		setOpenExportModal(true);
	};

	return (
		<>
			<div className="flex justify-between items-center mt-4 mb-8">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={
							activeTab === "loanBenefitGroups"
								? fetchedLoanBenefitGroupData
								: fetchedLoanBenefitData
						}
						setTableData={
							activeTab === "loanBenefitGroups"
								? setLoanBenefitGroupTableData
								: setLoanBenefitTableData
						}
						mutate={
							activeTab === "loanBenefitGroups"
								? searchLoanBenefitGroups
								: searchLoanBenefits
						}
					/>
					<TableFilter
						fields={
							activeTab === "loanBenefitGroups"
								? [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Code", fieldName: "code" },
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
									]
								: [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Code", fieldName: "code" },
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Loan Benefit Group",
											fieldName: "groupId",
											fieldType: "INPUT_FIELD",
											inputType: "number",
										},
									]
						}
					/>
					<ManageTableColumns
						columns={
							activeTab === "loanBenefitGroups"
								? (loanBenefitGroupColumns as any)
								: (loanBenefitColumns as any)
						}
						defaultVisibilityModel={
							activeTab === "loanBenefitGroups"
								? loanBenefitGroupColumnVisibility
								: loanBenefitColumnVisibility
						}
						visibilityModel={
							activeTab === "loanBenefitGroups"
								? loanBenefitGroupColumnVisibilityModel
								: loanBenefitColumnVisibilityModel
						}
						setVisibilityModel={
							activeTab === "loanBenefitGroups"
								? setLoanBenefitGroupColumnVisibilityModel
								: setLoanBenefitColumnVisibilityModel
						}
						tableApiRef={
							activeTab === "loanBenefitGroups"
								? loanBenefitGroupTableAPIRef
								: loanBenefitTableAPIRef
						}
					/>
				</div>
				<div className="flex items-center gap-4 font-medium">
					{/* Upload/Export Dropdown - Only show for Employee Loan Benefits tab */}
					{activeTab === "loanBenefitGroups" && (
						<PermissionChecker
							permissionRule="OR"
							permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
						>
							<button
								onClick={() => setOpenUploadLoanBenefitModal(true)}
								className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
							>
								<GoUpload className="mr-2 text-[17px] text-gray-500" />
								Upload Loan Benefit Groups
							</button>
						</PermissionChecker>
					)}

					{/* Export - Only show for Employee Allowances tab */}
					{activeTab === "loanBenefits" && (
						<button
							onClick={handleOpenExportModal}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<BsDownload className="mr-2 text-[18px] text-gray-500" />
							Export Loan Benefits
						</button>
					)}

					<PermissionChecker
						permissionRule="OR"
						permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
					>
						{activeTab === "loanBenefitGroups" ? (
							<button
								onClick={handleOpenLoanBenefitGroupModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Loan Benefit Group
							</button>
						) : (
							<button
								onClick={handleOpenLoanBenefitModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Employee Loan Benefit
							</button>
						)}
					</PermissionChecker>
				</div>
			</div>

			{/* Tabs */}
			<div className="flex border-b border-gray-200 mb-6">
				<button
					onClick={() => handleTabChange("loanBenefitGroups")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "loanBenefitGroups"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Loan Benefit Groups
				</button>
				<button
					onClick={() => handleTabChange("loanBenefits")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "loanBenefits"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Employee Loan Benefits
				</button>
			</div>

			{activeTab === "loanBenefitGroups" ? (
				<TanStackEmployeeTable
					ref={loanBenefitGroupTableAPIRef}
					tableId={TableId.LOAN_BENEFIT_GROUPS}
					data={loanBenefitGroupTableData}
					columns={loanBenefitGroupColumns}
					loading={loanBenefitGroupLoading}
					columnVisibility={loanBenefitGroupColumnVisibilityModel}
					onColumnVisibilityChange={setLoanBenefitGroupColumnVisibilityModel}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.target as HTMLButtonElement);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget);
					}}
					initialColumnVisibility={loanBenefitGroupColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([
						PERMISSIONS.COMPANY_CONFIGS_READ,
					])}
					pageCount={loanBenefitGroupPageCount}
					apiRef={loanBenefitGroupTableAPIRef}
				/>
			) : (
				<TanStackEmployeeTable
					ref={loanBenefitTableAPIRef}
					tableId={TableId.LOAN_BENEFITS}
					data={loanBenefitTableData}
					columns={loanBenefitColumns}
					loading={loanBenefitLoading}
					columnVisibility={loanBenefitColumnVisibilityModel}
					onColumnVisibilityChange={setLoanBenefitColumnVisibilityModel}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.target as HTMLButtonElement);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget);
					}}
					initialColumnVisibility={loanBenefitColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([
						PERMISSIONS.COMPANY_CONFIGS_READ,
					])}
					pageCount={loanBenefitPageCount}
					apiRef={loanBenefitTableAPIRef}
				/>
			)}

			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={
					activeTab === "loanBenefitGroups"
						? setOpenLoanBenefitGroupModal
						: setOpenLoanBenefitModal
				}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.name || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openLoanBenefitGroupModal && (
				<LoanBenefitGroupModalForm
					open={openLoanBenefitGroupModal}
					setOpen={setOpenLoanBenefitGroupModal}
					title={
						isEditForm
							? "Update Loan Benefit Group"
							: "Add New Loan Benefit Group"
					}
					isEditForm={isEditForm}
					tableAPIRef={loanBenefitGroupTableAPIRef}
					reloadTableData={reloadTableData}
				/>
			)}
			{openLoanBenefitModal && (
				<LoanBenefitModalForm
					open={openLoanBenefitModal}
					setOpen={setOpenLoanBenefitModal}
					title={isEditForm ? "Update Loan Benefit" : "Add New Loan Benefit"}
					isEditForm={isEditForm}
					tableAPIRef={loanBenefitTableAPIRef}
					employees={employees?.data || []}
					employeeBands={employeeBands?.data || []}
					loanBenefitGroups={loanBenefitGroups?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openUploadLoanBenefitModal && (
				<UploadLoanBenefit
					open={openUploadLoanBenefitModal}
					setOpen={setOpenUploadLoanBenefitModal}
				/>
			)}
			{openExportModal && currentCompany?.id && (
				<ExportLoanBenefitsModal
					open={openExportModal}
					setOpen={setOpenExportModal}
					companyId={currentCompany.id}
				/>
			)}
		</>
	);
};

export default LoanBenefitsView;